import { format, parseISO } from 'date-fns';
import pl from 'date-fns/locale/pl';
import _fileAPI from '../../../api/fileAPI';
import OCRDocumentAPI from '../../../api/ocr/OCRDocumentAPI';
import MailAttachmentsAPI from '../../../api/crm/MailAttachments';
import { SCANYE_DOCUMENT_STATUS_ERROR } from '../../constants/ocr/scanye/ScanyeOCR';
import { isOcrRetryBlocked } from '../../helpers/ocr-limits';
import {
  reconcileFileCollection,
  synchronizeSelectedFiles,
  updateFileCollection,
} from '../../helpers/ocr-file-state';

const fileAPI = new _fileAPI();

const state = {
  files: [],
  skippedFiles: [],
  isLoadingFiles: false,
  selectedRow: [],
  fetchCallback: undefined,
};

const getters = {
  filesByPeriod: (state) => {
    return state.files.reduce((files, file) => {
      const month = format(parseISO(file.due_date), 'LLLL', { locale: pl });
      const year = format(parseISO(file.due_date), 'yyyy');
      const key = `${ month } ${ year }`;

      files[key] = files[key] || [];
      files[key].push(file);

      return files;
    }, Object.create(null));
  },

  getNotAssignedFiles: (state) => {
    return state.files.filter(file => !state.skippedFiles.find(id => id === file.id) && !file.evidence);
  },
  getAssignedFiles: (state) => {
    return state.files.filter(file => !state.skippedFiles.find(id => id === file.id) && file.evidence);
  },
  getSkippedFiles(state) {
    return state.files.filter(el => state.skippedFiles.some(id => id === el.id));
  },
};

const actions = {
  index({ commit }, params) {
    return new Promise((resolve, reject) => {
      fileAPI.index(params)
        .then(({ data }) => {
          commit('setFiles', data.items);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  },
  store({ commit }, data) {
    fileAPI.store(data);
  },
  setFilesLoading({ commit }, isLoadingFiles) {
    commit('setFilesLoading', isLoadingFiles);
  },
  skipFile({ commit }, file) {
    commit('skipFile', file);
  },
  restoreFile({ commit }, file) {
    commit('restoreFile', file);
  },
  refreshSkippedFiles({ commit }) {
    commit('refreshSkippedFiles');
  },
  async bulkUpload({ commit }, items) {
    return await Promise.all(items.map(item => fileAPI.store({
      filename: item.filename,
      file: item.file,
      pages: item.pages,
      due_date: item.due_date,
    })));
  },
  async bulkDelete({ commit }, ids) {
    return await Promise.all(ids.map(id => fileAPI.delete(id)));
  },
  async bulkOCR({ commit }, ids) {
    return await Promise.all(ids.map(id => OCRDocumentAPI.obtainOCR(id)));
  },
  async bulkScan({}, files) {
    const scannable = files.filter(file => file._is_ocrable && !isOcrRetryBlocked(file));

    return await Promise.all(scannable.map(file => SCANYE_DOCUMENT_STATUS_ERROR === file?.ocr?.status
      ? OCRDocumentAPI.retryOCR(file.id)
      : OCRDocumentAPI.obtainOCR(file.id)));
  },
  async bulkOCRSequential({}, ids) {
    for (const id of ids) {
      await OCRDocumentAPI.obtainOCR(id);
    }
  },
  indexMailAttachments({ commit }, params) {
    return new Promise((resolve, reject) => {
      MailAttachmentsAPI.getMailAttachments(params)
        .then(({ data }) => {
          commit('setFiles', data.items);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  },
};

const mutations = {
  setFiles(state, files) {
    state.files = reconcileFileCollection(state.files, files);
    state.selectedRow = synchronizeSelectedFiles(state.selectedRow, state.files);
  },
  updateFile(state, file) {
    state.files = updateFileCollection(state.files, file);
    state.selectedRow = synchronizeSelectedFiles(state.selectedRow, state.files);
  },
  upsertFile(state, file) {
    state.files = updateFileCollection(state.files, file, true);
    state.selectedRow = synchronizeSelectedFiles(state.selectedRow, state.files);
  },
  addSelectedRow(state, file) {
    state.selectedRow.push(file);
  },
  removeSelectedRow(state, file) {
    state.selectedRow = state.selectedRow.filter(f => f.id !== file.id);
  },
  setSelectedRows(state, items) {
    state.selectedRow = items;
  },
  setFilesLoading(state, isLoadingFiles) {
    state.isLoadingFiles = isLoadingFiles;
  },
  skipFile(state, file) {
    state.skippedFiles.push(file.id);
  },
  restoreFile(state, file) {
    const index = state.skippedFiles.findIndex(el => {
      return el === file.id;
    });
    state.skippedFiles.splice(index, 1);
  },
  refreshSkippedFiles(state) {
    state.skippedFiles = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
